import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Contacts (Marketing/Leads) — `GET /contacts` and related stat endpoints.
 * Field names are lifted verbatim from the v4 backend (`modules/customer/customer.marketing`).
 * Shapes verified live against api-labs-datacentral.vnetwork.dev.
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface ContactAssigneeName {
  employee_id: number;
  employee_name: string;
  employee_email?: string;
}

export interface ContactMetadata {
  source_url?: string;
  host?: string;
  currentUrl?: string;
  referer?: string;
  tracking?: string;
  country?: string;
  [key: string]: unknown;
}

/** One row from `GET /contacts` or `GET /contacts/my-leads`. */
export interface ContactRow {
  contact_source_id: number;
  contact_source_name: string | null;
  contact_source_email: string | null;
  contact_source_type: string | null;
  contact_source_channel: string | null;
  contact_source_company: string | null;
  contact_source_assignee_ids: number[];
  contact_source_assignee_name: ContactAssigneeName[] | null;
  contact_source_assigned_at: string | null;
  contact_source_score: number | null;
  contact_source_verified: boolean | null;
  contact_source_referrer_id: number | null;
  contact_source_referrer_type: string | null;
  contact_source_object_id: string | null;
  contact_source_object_type: string | null;
  contact_source_properties: Record<string, unknown> | null;
  metadata: ContactMetadata | null;
  website_referrer: string | null;
  contact_source_verified_at: string | null;
  contact_source_created_at: string;
  contact_source_updated_at: string;
  contact_source_phone: string | null;
  contact_source_website: string | null;
  contact_source_notes: string | null;
  contact_source_status: string | null;
  contact_source_position: string | null;
  contact_source_profile: string | null;
  contact_source_industry: string | null;
  contact_source_interested_services: string[];
  converted: boolean;
  revenue: number | null;
  current_journey_step: string | null;
  current_journey_step_status: string | null;
  current_journey_id: number | null;
  last_journey_note: string | null;
  last_journey_note_at: string | null;
  company_size: string | null;
  deployment_time: string | null;
  specific_needs: string | null;
  deleted_at: string | null;
}

/** `GET /contacts/:id` detail record (same shape, no journey fields). */
export type ContactDetail = Omit<
  ContactRow,
  | 'current_journey_step'
  | 'current_journey_step_status'
  | 'current_journey_id'
  | 'last_journey_note'
  | 'last_journey_note_at'
  | 'converted'
  | 'revenue'
>;

/** `GET /contacts/statistic` — lead pipeline status counts. */
export interface ContactStatistic {
  total: number;
  unclassified: number;
  warm_lead: number;
  sale_qualified_lead: number;
  cool_lead: number;
  un_qualified_lead: number;
  converted: number;
  unverified: number;
  unassigned: number;
  assigned: number;
  total_revenue: number;
}

/** `GET /contacts/statistic/conversion-rate` */
export interface ConversionRateStats {
  total_leads: number;
  unclassified: number;
  warm_lead: number;
  sale_qualified_lead: number;
  cool_lead: number;
  un_qualified_lead: number;
  total_classified: number;
  classification_rate: number;
  warm_lead_rate: number;
  sale_qualified_lead_rate: number;
  cool_lead_rate: number;
  un_qualified_rate: number;
  warm_conversion_rate: number;
  sale_qualified_conversion_rate: number;
  cool_conversion_rate: number;
  un_qualified_conversion_rate: number;
}

/** Row from `GET /contacts/statistic/by-channel`. */
export interface ChannelStats {
  channel: string;
  total: number;
  unclassified: number;
  warm_lead: number;
  sale_qualified_lead: number;
  cool_lead: number;
  un_qualified_lead: number;
}

/** Row from `GET /contacts/statistic/by-product-group`. */
export interface ProductGroupStats {
  product_group: string;
  total: number;
  unclassified: number;
  warm_lead: number;
  sale_qualified_lead: number;
  cool_lead: number;
  un_qualified_lead: number;
}

/** Row from `GET /contacts/statistic/by-assignee`. */
export interface AssigneeStats {
  employee_id: number;
  employee_name: string;
  employee_email_company: string;
  total: number;
  unclassified: number;
  warm_lead: number;
  sale_qualified_lead: number;
  cool_lead: number;
  un_qualified_lead: number;
}

/* ── Query params ────────────────────────────────────────────────────────── */

export interface ContactListParams {
  page?: number;
  page_size?: number;
  q?: string;
  /** Pre-encoded base64 JSON filters object (same as customers). */
  filters?: string;
}

/* ── API functions ──────────────────────────────────────────────────────── */

async function fetchContacts(params: ContactListParams): Promise<ResponseArray<ContactRow>> {
  const query = qs.stringify(
    { page: params.page ?? 1, limit: params.page_size ?? 20, q: params.q, filters: params.filters },
    { skipNull: true, skipEmptyString: true },
  );
  const res = await dataCentralApi.get<ResponseArray<ContactRow>>(`/contacts?${query}`);
  return res.data;
}

async function fetchMyLeads(params: ContactListParams): Promise<ResponseArray<ContactRow>> {
  const query = qs.stringify(
    { page: params.page ?? 1, limit: params.page_size ?? 20, q: params.q, filters: params.filters },
    { skipNull: true, skipEmptyString: true },
  );
  const res = await dataCentralApi.get<ResponseArray<ContactRow>>(`/contacts/my-leads?${query}`);
  return res.data;
}

async function fetchContactById(id: number): Promise<ResponseObject<ContactDetail>> {
  const res = await dataCentralApi.get<ResponseObject<ContactDetail>>(`/contacts/${id}`);
  return res.data;
}

async function fetchContactStatistic(): Promise<ResponseObject<ContactStatistic>> {
  const res = await dataCentralApi.get<ResponseObject<ContactStatistic>>('/contacts/statistic');
  return res.data;
}

async function fetchConversionRateStats(): Promise<ResponseObject<ConversionRateStats>> {
  const res = await dataCentralApi.get<ResponseObject<ConversionRateStats>>(
    '/contacts/statistic/conversion-rate',
  );
  return res.data;
}

async function fetchChannelStats(): Promise<{ message: string; results: ChannelStats[] }> {
  const res = await dataCentralApi.get<{ message: string; results: ChannelStats[] }>(
    '/contacts/statistic/by-channel',
  );
  return res.data;
}

async function fetchProductGroupStats(): Promise<{ message: string; results: ProductGroupStats[] }> {
  const res = await dataCentralApi.get<{ message: string; results: ProductGroupStats[] }>(
    '/contacts/statistic/by-product-group',
  );
  return res.data;
}

async function fetchAssigneeStats(): Promise<{ message: string; results: AssigneeStats[] }> {
  const res = await dataCentralApi.get<{ message: string; results: AssigneeStats[] }>(
    '/contacts/statistic/by-assignee',
  );
  return res.data;
}

/* ── React Query hooks ──────────────────────────────────────────────────── */

export function useContacts(params: ContactListParams) {
  return useQuery({
    queryKey: ['contacts', params],
    queryFn: () => fetchContacts(params),
  });
}

export function useMyLeads(params: ContactListParams) {
  return useQuery({
    queryKey: ['contacts', 'my-leads', params],
    queryFn: () => fetchMyLeads(params),
  });
}

export function useContactById(id: number) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => fetchContactById(id),
    enabled: id > 0,
  });
}

export function useContactStatistic() {
  return useQuery({
    queryKey: ['contacts', 'statistic'],
    queryFn: fetchContactStatistic,
  });
}

export function useConversionRateStats() {
  return useQuery({
    queryKey: ['contacts', 'statistic', 'conversion-rate'],
    queryFn: fetchConversionRateStats,
  });
}

export function useChannelStats() {
  return useQuery({
    queryKey: ['contacts', 'statistic', 'by-channel'],
    queryFn: fetchChannelStats,
  });
}

export function useProductGroupStats() {
  return useQuery({
    queryKey: ['contacts', 'statistic', 'by-product-group'],
    queryFn: fetchProductGroupStats,
  });
}

export function useAssigneeStats() {
  return useQuery({
    queryKey: ['contacts', 'statistic', 'by-assignee'],
    queryFn: fetchAssigneeStats,
  });
}

/* ── Filter helpers ──────────────────────────────────────────────────────── */

/**
 * Build the base64-encoded `filters` query param for the contacts list API.
 * Accepts a plain object of filter conditions — same pattern as customers.
 */
export function buildContactFilters(obj: Record<string, unknown>): string {
  return toFilters(obj);
}
